/**
 * useComments — fetches comments for a specific task, with realtime
 * INSERT subscription. Comments are immutable (no UPDATE/DELETE per
 * BIT 9), so we only need to watch for INSERTs.
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../api/supabase.js';

export function useComments(taskId) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchComments = useCallback(async () => {
    if (!taskId) {
      setComments([]);
      setLoading(false);
      return;
    }
    setError(null);
    const { data, error: fetchError } = await supabase
      .from('comments')
      .select(`
        *,
        author:users!author_id(id, full_name, role, avatar_url)
      `)
      .eq('task_id', taskId)
      .order('created_at', { ascending: true });

    if (fetchError) {
      console.error('[ORIN] Failed to fetch comments:', fetchError.message);
      setError(fetchError.message);
      setComments([]);
    } else {
      setComments(data || []);
    }
    setLoading(false);
  }, [taskId]);

  useEffect(() => {
    let mounted = true;
    fetchComments();

    if (!taskId) return undefined;

    const channel = supabase
      .channel(`comments-${taskId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'comments', filter: `task_id=eq.${taskId}` },
        () => {
          if (mounted) fetchComments();
        },
      )
      .subscribe();

    return () => {
      mounted = false;
      supabase.removeChannel(channel);
    };
  }, [taskId, fetchComments]);

  return { comments, loading, error, refresh: fetchComments };
}
